import { format } from 'date-fns'
import startCase from 'lodash/startCase'

import type {
  MdCase,
  MdCasePromotionState,
  MdCaseSeverity,
  MdCaseSourceType,
  MdCaseStatus,
  MdCaseWorkflowMode,
} from './mdCase'

export type MdCasesPublishedStateDisplay = 'published' | 'unpublished'

export const getMdCaseWorkflowModeLabel = (
  workflowMode: MdCaseWorkflowMode | null
): string => (workflowMode ? startCase(workflowMode) : 'Unknown')

export const getMdCaseSourceTypeLabel = (
  sourceType: MdCaseSourceType | null
): string => (sourceType ? startCase(sourceType) : 'Unknown')

export const getMdCasePromotionStateLabel = (
  promotionState: MdCasePromotionState | null
): string => {
  if (promotionState === 'sent_to_site') {
    return 'Sent To Site'
  }

  if (promotionState === 'roc_only') {
    return 'ROC Only'
  }

  return 'Unknown'
}

export const getMdCasesPublishedStateLabel = (
  publishedState: MdCasesPublishedStateDisplay
): string => (publishedState === 'published' ? 'Published' : 'Unpublished')

// ── Severity tokens ──

export const SEVERITY_TOKENS = {
  High: { tone: 'danger' as const, color: '#f25555', rank: 0 },
  Medium: { tone: 'warning' as const, color: '#d48f31', rank: 1 },
  Low: { tone: 'accent' as const, color: '#3cab67', rank: 2 },
} as const

export type MdCaseTone =
  | 'default'
  | 'danger'
  | 'warning'
  | 'success'
  | 'accent'
  | 'muted'

const PUBLISHED_STATE_TOKENS = {
  published: { tone: 'success' as const, rank: 0 },
  unpublished: { tone: 'muted' as const, rank: 1 },
} as const

export const getPublishedStateTone = (
  publishedState: MdCasesPublishedStateDisplay
): MdCaseTone => PUBLISHED_STATE_TOKENS[publishedState].tone

export const getPublishedStateRank = (
  publishedState: MdCasesPublishedStateDisplay
): number => PUBLISHED_STATE_TOKENS[publishedState].rank

export const getSeverityTone = (severity: MdCaseSeverity | null): MdCaseTone =>
  severity === null ? 'default' : SEVERITY_TOKENS[severity].tone

export const getSeverityColor = (severity: MdCaseSeverity | null): string =>
  severity === null ? '#888' : SEVERITY_TOKENS[severity].color

export const getSeverityRank = (severity: MdCaseSeverity | null): number =>
  severity === null ? Number.MAX_SAFE_INTEGER : SEVERITY_TOKENS[severity].rank

// ── Status tokens ──

export const STATUS_TOKENS = {
  'New Alert': { tone: 'warning' as const, rank: 0, isArchived: false },
  Open: { tone: 'success' as const, rank: 1, isArchived: false },
  Resolved: { tone: 'accent' as const, rank: 2, isArchived: false },
  Closed: { tone: 'muted' as const, rank: 3, isArchived: true },
  Cleared: { tone: 'muted' as const, rank: 4, isArchived: true },
  Shelved: { tone: 'default' as const, rank: 5, isArchived: false },
} as const

export const getStatusTone = (status: MdCaseStatus | null): MdCaseTone =>
  status === null ? 'default' : STATUS_TOKENS[status].tone

export const getStatusRank = (status: MdCaseStatus | null): number =>
  status === null ? Number.MAX_SAFE_INTEGER : STATUS_TOKENS[status].rank

// ── Date formatting ──

export const formatMdCaseDisplayDateTime = (
  value: string | null | undefined
): string => {
  if (!value) return 'Unknown'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? value
    : format(date, 'MMM d, yyyy · h:mm a')
}

export const formatMdCaseDisplayCompactDate = (
  value: string | null | undefined
): string => {
  if (!value) return 'Unknown'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return value
  }

  const now = new Date()
  return format(
    date,
    date.getFullYear() === now.getFullYear() ? 'MMM d' : 'MMM d, yyyy'
  )
}

export const formatMdCaseDateTimeRange = (from: string, to: string): string =>
  `${formatMdCaseDisplayDateTime(from)} -> ${formatMdCaseDisplayDateTime(to)}`

// ── Publish state label ──

export const getMdCasePublishStateLabel = (mdCase: MdCase): string => {
  if (
    mdCase.workflow.published_at ||
    mdCase.workflow.promotion_state === 'sent_to_site'
  ) {
    return 'Published'
  }

  if (mdCase.workflow.promotion_state === 'roc_only') {
    return 'ROC Only'
  }

  return 'Draft'
}
